import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { DateTime } from "luxon";
import InputDatePicker from "app/components/DatePicker/InputDatePicker";
import localStyles from "./styles.module.scss";
import { HolidayResponse, HolidayPayload } from "app/utils/request/scheduling/facilityHolidays";

interface HolidaysViewProps {
  holidays: HolidayResponse[];
  onAdd: (payload: HolidayPayload) => Promise<void>;
  onEdit: (holidayId: number, payload: HolidayPayload) => Promise<void>;
  onDelete: (holidayId: number) => Promise<void>;
}

const HolidaysView: React.FC<HolidaysViewProps> = ({
  holidays,
  onAdd,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();

  // Add form state
  const [newName, setNewName] = useState("");
  const [newDate, setNewDate] = useState<DateTime | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  // Edit mode state
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const [editDate, setEditDate] = useState<DateTime | null>(null);

  // Format date for display (e.g., "Dec 25, 2026")
  const formatDisplayDate = (dateString: string): string => {
    const date = DateTime.fromISO(dateString);
    return date.toLocaleString({ month: "short", day: "numeric", year: "numeric" });
  };

  // Handle add holiday
  const handleAddSubmit = async () => {
    if (!newName.trim() || !newDate) {
      return;
    }

    setIsAdding(true);
    try {
      const payload: HolidayPayload = {
        name: newName.trim(),
        date: newDate.toISODate() || "",
      };
      await onAdd(payload);
      // Clear form
      setNewName("");
      setNewDate(null);
    } finally {
      setIsAdding(false);
    }
  };

  // Handle edit start
  const handleEditStart = (holiday: HolidayResponse) => {
    setEditingId(holiday.id);
    setEditName(holiday.name);
    setEditDate(DateTime.fromISO(holiday.date));
  };

  // Handle edit save
  const handleEditSave = async (holidayId: number) => {
    if (!editName.trim() || !editDate) {
      return;
    }

    try {
      const payload: HolidayPayload = {
        name: editName.trim(),
        date: editDate.toISODate() || "",
      };
      await onEdit(holidayId, payload);
      // Exit edit mode
      setEditingId(null);
      setEditName("");
      setEditDate(null);
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  // Handle edit cancel
  const handleEditCancel = () => {
    setEditingId(null);
    setEditName("");
    setEditDate(null);
  };

  // Handle delete
  const handleDelete = async (holidayId: number) => {
    await onDelete(holidayId);
  };

  const isAddFormValid = newName.trim().length > 0 && newDate !== null;

  return (
    <div className={localStyles.holidays_container}>
      {/* Add Holiday Form */}
      <div className={localStyles.add_form}>
        <div className={localStyles.add_form_fields}>
          <input
            type="text"
            className={localStyles.input_name}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={t("Holiday name")}
          />
          <div className={localStyles.date_picker_wrapper}>
            <InputDatePicker
              date={newDate}
              setDate={setNewDate}
              placeholderText={t("Select Date")}
              minDate={new Date()}
            />
          </div>
          <button
            className={localStyles.add_button}
            onClick={handleAddSubmit}
            disabled={!isAddFormValid || isAdding}
          >
            {isAdding ? t("Adding...") : t("Add Holiday")}
          </button>
        </div>
      </div>

      {/* Holiday List */}
      {holidays.length > 0 && (
        <div className={localStyles.holidays_list}>
          {holidays.map((holiday) => {
            const isEditing = editingId === holiday.id;

            return (
              <div key={holiday.id} className={localStyles.holiday_row}>
                {isEditing ? (
                  <div className={localStyles.edit_form}>
                    <input
                      type="text"
                      className={localStyles.input_name}
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      placeholder={t("Holiday name")}
                    />
                    <div className={localStyles.date_picker_wrapper}>
                      <InputDatePicker
                        date={editDate}
                        setDate={setEditDate}
                        placeholderText={t("Select Date")}
                        minDate={new Date()}
                      />
                    </div>
                    <div className={localStyles.edit_buttons}>
                      <button
                        className={localStyles.save_button}
                        onClick={() => handleEditSave(holiday.id)}
                        disabled={!editName.trim() || !editDate}
                      >
                        {t("Save")}
                      </button>
                      <button
                        className={localStyles.cancel_button}
                        onClick={handleEditCancel}
                      >
                        {t("Cancel")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <span className={localStyles.holiday_name}>
                      {holiday.name}
                    </span>
                    <span className={localStyles.holiday_date}>
                      {formatDisplayDate(holiday.date)}
                    </span>
                    <div className={localStyles.holiday_actions}>
                      <button
                        className={localStyles.edit_button}
                        onClick={() => handleEditStart(holiday)}
                      >
                        <i className="ico ico-pencil" />
                      </button>
                      <button
                        className={localStyles.delete_button}
                        onClick={() => handleDelete(holiday.id)}
                      >
                        <i className="ico ico-trash" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Empty state */}
      {holidays.length === 0 && (
        <div className={localStyles.empty_state}>
          <p>{t("No holidays defined yet.")}</p>
        </div>
      )}
    </div>
  );
};

export default HolidaysView;
